import React, { useState, useRef, useEffect } from 'react';
import { lbl, inp } from './shared';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY',
];

async function searchAddress(query) {
  const res = await fetch(`/api/address-search?q=${encodeURIComponent(query)}`);
  if (!res.ok) return [];
  return res.json();
}

function formatPhone(val) {
  const digits = val.replace(/\D/g, '').slice(0, 10);
  if (digits.length < 4) return digits;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function AddressAutocomplete({ label, value, prefix, onChange, hint, showCounty }) {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setSuggestions([]);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  function handleType(val) {
    setQuery(val);
    onChange(`${prefix}address`, val);
    clearTimeout(debounceRef.current);
    if (val.length < 4) { setSuggestions([]); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const results = await searchAddress(val);
        setSuggestions(results);
      } catch {
        setSuggestions([]);
      }
      setLoading(false);
    }, 350);
  }

  function handleSelect(s) {
    setQuery(s.street || s.display);
    setSuggestions([]);
    onChange(`${prefix}address`, s.street || s.display);
    if (s.city)   onChange(`${prefix}city`, s.city);
    if (s.state)  onChange(`${prefix}state`, s.state);
    if (s.zip)    onChange(`${prefix}zip`, s.zip);
    if (showCounty && s.county) onChange('county', s.county);
  }

  return (
    <div style={{ marginBottom: 18 }} ref={containerRef}>
      <label style={lbl}>
        {label}
        {loading && <span style={{ fontWeight: 400, color: '#7a99b8', marginLeft: 8, textTransform: 'none', letterSpacing: 0 }}>Searching…</span>}
      </label>
      <div style={{ position: 'relative' }}>
        <input
          style={inp}
          name={`${prefix || 'physical'}-street-${label.replace(/\s+/g, '-').toLowerCase()}`}
          value={query}
          onChange={e => handleType(e.target.value)}
          placeholder="Start typing your address…"
          autoComplete="new-password"
          autoCorrect="off"
          spellCheck={false}
        />
        {suggestions.length > 0 && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0,
            background: '#fff', border: '1.5px solid #c5d5e8', borderRadius: 8,
            zIndex: 200, maxHeight: 220, overflowY: 'auto',
            boxShadow: '0 6px 16px rgba(26,51,100,0.13)',
            marginTop: 2,
          }}>
            {suggestions.map((s, i) => (
              <div
                key={i}
                onMouseDown={() => handleSelect(s)}
                style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 13, color: '#1a3364', borderBottom: i < suggestions.length - 1 ? '1px solid #eef2f7' : 'none' }}
                onMouseEnter={e => e.currentTarget.style.background = '#dbeef8'}
                onMouseLeave={e => e.currentTarget.style.background = '#fff'}
              >
                <div style={{ fontWeight: 600 }}>{s.street || s.display.split(',')[0]}</div>
                <div style={{ color: '#7a99b8', fontSize: 12, marginTop: 2 }}>
                  {[s.city, s.state, s.zip].filter(Boolean).join(', ')}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {hint && (
        <p style={{ fontSize: 12, color: '#7a99b8', margin: '5px 0 0' }}>{hint}</p>
      )}
    </div>
  );
}

export default function ContactInfo({ data, onChange }) {
  const mailingIsSame = data.mailingIsSameAsPhysical !== false;

  return (
    <div>
      {/* Physical Address */}
      <AddressAutocomplete
        label="Physical Address"
        value={data.address}
        prefix=""
        onChange={onChange}
        hint="Type your street address and select from suggestions to auto-fill city, state, and ZIP."
        showCounty
      />

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
        <div>
          <label style={lbl}>City</label>
          <input style={inp} value={data.city} onChange={e => onChange('city', e.target.value)} />
        </div>
        <div>
          <label style={lbl}>State</label>
          <select style={inp} value={data.state} onChange={e => onChange('state', e.target.value)}>
            <option value="">--</option>
            {US_STATES.map(st => <option key={st} value={st}>{st}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>ZIP</label>
          <input style={inp} maxLength={10} value={data.zip} onChange={e => onChange('zip', e.target.value)} />
        </div>
      </div>

      <div style={{ marginBottom: 18 }}>
        <label style={lbl}>County</label>
        <input style={inp} value={data.county} onChange={e => onChange('county', e.target.value)} />
      </div>

      {/* Mailing address toggle */}
      <div style={{ marginBottom: 18 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
          <input
            type="checkbox"
            checked={mailingIsSame}
            onChange={e => onChange('mailingIsSameAsPhysical', e.target.checked)}
            style={{ width: 17, height: 17, cursor: 'pointer', accentColor: '#1a3364' }}
          />
          <span style={{ fontSize: 14, color: '#1a3364', fontWeight: 500 }}>
            My mailing address is the same as my physical address
          </span>
        </label>
      </div>

      {/* Mailing Address — shown only when different */}
      {!mailingIsSame && (
        <div style={{ background: '#f4f8fc', border: '1.5px solid #c5d5e8', borderRadius: 10, padding: '16px 18px', marginBottom: 18 }}>
          <AddressAutocomplete
            label="Mailing Address"
            value={data.mailingAddress}
            prefix="mailing"
            onChange={onChange}
            hint="For a P.O. Box, type it directly (e.g. PO Box 123) — no need to select a suggestion."
          />
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 14 }}>
            <div>
              <label style={lbl}>City</label>
              <input style={inp} value={data.mailingCity} onChange={e => onChange('mailingCity', e.target.value)} />
            </div>
            <div>
              <label style={lbl}>State</label>
              <select style={inp} value={data.mailingState} onChange={e => onChange('mailingState', e.target.value)}>
                <option value="">--</option>
                {US_STATES.map(st => <option key={st} value={st}>{st}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>ZIP</label>
              <input style={inp} maxLength={10} value={data.mailingZip} onChange={e => onChange('mailingZip', e.target.value)} />
            </div>
          </div>
        </div>
      )}

      <div style={{ marginBottom: 18 }}>
        <label style={lbl}>Email Address</label>
        <input style={inp} type="email" value={data.email} onChange={e => onChange('email', e.target.value)} />
      </div>

      <div style={{ marginBottom: 18 }}>
        <label style={lbl}>Phone Number</label>
        <input
          style={inp}
          type="tel"
          placeholder="(555) 000-0000"
          value={data.phone}
          onChange={e => onChange('phone', formatPhone(e.target.value))}
        />
      </div>
    </div>
  );
}
